import AbstractSegment from "./AbstractSegment";
import ControlPoint from "../ControlPoint";

export default class BezierSegment extends AbstractSegment {
    constructor(provider, start, end, parent, index, cp1X, cp1Y, cp2X, cp2Y) {
        super(provider, start, end, parent, index);
        this.cp1 = new ControlPoint(provider, cp1X, cp1Y, this, 'start');
        this.cp2 = new ControlPoint(provider, cp2X, cp2Y, this, 'end');
    }

    get type() {
        return 'bezier';
    }

    display(g) {
        if (this.index === 0) {
            g.vertex(this.start.currentX, this.start.currentY);
        }
        g.bezierVertex(
            this.cp1.currentX, this.cp1.currentY,
            this.cp2.currentX, this.cp2.currentY,
            this.end.currentX, this.end.currentY
        );
    }

    displayHighlight(ctx) {
        if (this.index === 0) {
            ctx.moveTo(this.start.currentX, this.start.currentY);
        }
        ctx.bezierCurveTo(
            this.cp1.currentX, this.cp1.currentY,
            this.cp2.currentX, this.cp2.currentY,
            this.end.currentX, this.end.currentY
        );
    }

    displayHover(g) {
        let points = this.sampleCurrentPoints(20);
        for (let i = 0; i < points.length - 1; i++) {
            g.line(points[i].x, points[i].y, points[i + 1].x, points[i + 1].y);
        }
    }

    hovered(viewX, viewY, tolerance) {
        let points = this.sampleCurrentPoints(20);
        for (let i = 0; i < points.length - 1; i++) {
            if (this.pointToSegmentDist(viewX, viewY, points[i].x, points[i].y, points[i + 1].x, points[i + 1].y) <= tolerance) {
                return true;
            }
        }
        return false;
    }

    length() {
        let points = this.samplePoints(20);
        let total = 0;
        let prev = { x: this.start.absX, y: this.start.absY };
        for (let pt of points) {
            let dx = pt.x - prev.x;
            let dy = pt.y - prev.y;
            total += Math.sqrt(dx * dx + dy * dy);
            prev = pt;
        }
        return this.start.calculatePixelToDisplayDistance(total);
    }

    midpoint() {
        let t = 0.5;
        let mt = 1 - t;
        return {
            absX: mt * mt * mt * this.start.absX + 3 * mt * mt * t * this.cp1.absX + 3 * mt * t * t * this.cp2.absX + t * t * t * this.end.absX,
            absY: mt * mt * mt * this.start.absY + 3 * mt * mt * t * this.cp1.absY + 3 * mt * t * t * this.cp2.absY + t * t * t * this.end.absY,
        };
    }

    export(dx, dy) {
        return [
            this.end.absX + dx,
            this.end.absY + dy,
            "bezier",
            this.cp1.absX + dx,
            this.cp1.absY + dy,
            this.cp2.absX + dx,
            this.cp2.absY + dy,
        ];
    }

    exportClosing() {
        return ["closing", "bezier", this.cp1.absX, this.cp1.absY, this.cp2.absX, this.cp2.absY];
    }

    getControlPoints() {
        return [this.cp1, this.cp2];
    }

    samplePoints(n) {
        let points = [];
        for (let i = 1; i <= n; i++) {
            let t = i / n;
            let mt = 1 - t;
            points.push({
                x: mt * mt * mt * this.start.absX + 3 * mt * mt * t * this.cp1.absX + 3 * mt * t * t * this.cp2.absX + t * t * t * this.end.absX,
                y: mt * mt * mt * this.start.absY + 3 * mt * mt * t * this.cp1.absY + 3 * mt * t * t * this.cp2.absY + t * t * t * this.end.absY,
            });
        }
        return points;
    }

    sampleCurrentPoints(n) {
        let points = [{ x: this.start.currentX, y: this.start.currentY }];
        for (let i = 1; i <= n; i++) {
            let t = i / n;
            let mt = 1 - t;
            points.push({
                x: mt * mt * mt * this.start.currentX + 3 * mt * mt * t * this.cp1.currentX + 3 * mt * t * t * this.cp2.currentX + t * t * t * this.end.currentX,
                y: mt * mt * mt * this.start.currentY + 3 * mt * mt * t * this.cp1.currentY + 3 * mt * t * t * this.cp2.currentY + t * t * t * this.end.currentY,
            });
        }
        return points;
    }

    pointToSegmentDist(px, py, x1, y1, x2, y2) {
        let dx = x2 - x1, dy = y2 - y1;
        let lenSq = dx * dx + dy * dy;
        if (lenSq === 0) return Math.sqrt((px - x1) * (px - x1) + (py - y1) * (py - y1));
        let t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq));
        let projX = x1 + t * dx, projY = y1 + t * dy;
        return Math.sqrt((px - projX) * (px - projX) + (py - projY) * (py - projY));
    }

    static defaultControlPoints(start, end) {
        let dx = end.absX - start.absX;
        let dy = end.absY - start.absY;
        let len = Math.sqrt(dx * dx + dy * dy);
        let nx = -dy / len, ny = dx / len;
        let offset = len * 0.25;
        return {
            cp1: {
                x: start.absX + dx / 3 + nx * offset,
                y: start.absY + dy / 3 + ny * offset,
            },
            cp2: {
                x: start.absX + 2 * dx / 3 + nx * offset,
                y: start.absY + 2 * dy / 3 + ny * offset,
            },
        };
    }
}
